import { useEffect, useRef } from "react";

const SCRIPT_ID = "google-recaptcha-script";

function RecaptchaWidget({ siteKey, onToken, theme = "dark", size = "normal", resetKey = 0 }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const resolveRecaptchaApi = () => {
    if (!window.grecaptcha) {
      return null;
    }
    if (typeof window.grecaptcha.render === "function") {
      return window.grecaptcha;
    }
    if (
      window.grecaptcha.enterprise &&
      typeof window.grecaptcha.enterprise.render === "function"
    ) {
      return window.grecaptcha.enterprise;
    }
    return null;
  };

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return;
    }

    let attempts = 0;
    let retryTimer = null;
    let active = true;
    const MAX_ATTEMPTS = 300; // ~60s at 200ms interval

    const renderWidget = () => {
      if (!active || widgetIdRef.current !== null) {
        return;
      }
      const recaptchaApi = resolveRecaptchaApi();
      if (!recaptchaApi || !containerRef.current) {
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          retryTimer = window.setTimeout(renderWidget, 200);
        }
        return;
      }
      try {
        widgetIdRef.current = recaptchaApi.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(""),
          "error-callback": () => onToken(""),
        });
      } catch (e) {
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          retryTimer = window.setTimeout(renderWidget, 200);
        }
      }
    };

    const onReady = () => {
      const recaptchaApi = resolveRecaptchaApi();
      if (!recaptchaApi) {
        return;
      }
      if (typeof recaptchaApi.ready === "function") {
        recaptchaApi.ready(renderWidget);
      } else {
        renderWidget();
      }
    };

    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = onReady;
      document.body.appendChild(script);
    } else {
      script.addEventListener("load", onReady);
    }

    onReady();
    renderWidget();

    return () => {
      active = false;
      script?.removeEventListener("load", onReady);
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [siteKey, onToken, theme, size]);

  useEffect(() => {
    const recaptchaApi = resolveRecaptchaApi();
    if (recaptchaApi && widgetIdRef.current !== null) {
      recaptchaApi.reset(widgetIdRef.current);
      onToken("");
    }
  }, [resetKey, onToken]);

  return <div ref={containerRef} />;
}

export default RecaptchaWidget;
